import { getCoreRowModel, getExpandedRowModel } from "@tanstack/react-table"
import type { ExpandedState, OnChangeFn, Row, SortingState } from "@tanstack/react-table"
import type { FC, Ref } from "react"
import { useState } from "react"

import { useInvoicesColumns } from "../hooks/useInvoicesColumns"

import { InvoiceRowDetails } from "./InvoiceRowDetails"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Invoice, InvoiceAs400SyncKind } from "@/graphql/codegen/graphql"

interface InvoicesTableProps {
  data: Invoice[]
  sorting: SortingState
  onSortingChange: OnChangeFn<SortingState>
  loading: boolean
  error?: string | null
  scrollHandler: () => void
  tableContainerRef?: Ref<HTMLDivElement>
  onEdit?: (row: Invoice) => void
  onView?: (row: Invoice) => void
  onCancel?: (row: Invoice) => void
  onVoid?: (row: Invoice) => void
  onConfirmAs400Reconciliation?: (row: Invoice, syncKind: InvoiceAs400SyncKind) => void
}

export const InvoicesTable: FC<InvoicesTableProps> = ({
  data,
  sorting,
  onSortingChange,
  loading,
  error,
  scrollHandler,
  tableContainerRef,
  onEdit,
  onView,
  onCancel,
  onVoid,
  onConfirmAs400Reconciliation,
}) => {
  const columns = useInvoicesColumns({ onEdit, onView, onCancel, onVoid, onConfirmAs400Reconciliation })
  const [expandedState, setExpandedState] = useState<ExpandedState>({})

  const renderExpandedContent = (row: Row<Invoice>) => <InvoiceRowDetails invoice={row.original} />

  return (
    <AerosBaseTable<Invoice>
      error={error ? { message: error } : undefined}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          state: {
            sorting,
            isLoading: loading,
            expanded: expandedState,
          },
          manualSorting: true,
          enableSorting: true,
          enableMultiSort: false,
          enableSortingRemoval: false,
          getCoreRowModel: getCoreRowModel(),
          getRowId: (row) => row.invoiceId,
          onSortingChange,
          onExpandedChange: setExpandedState,
          getRowCanExpand: () => true,
          getExpandedRowModel: getExpandedRowModel(),
          renderExpandedContent,
        },
      }}
      slotProps={{
        container: {
          ref: tableContainerRef,
          onScroll: scrollHandler,
          className: "overflow-auto",
        },
        bodyRow: {
          className: "hover:bg-gray-50",
        },
        bodyCellExpanded: { className: "bg-primary-100" },
      }}
      noDataMessage='No invoices found'
    />
  )
}
